/**
 * Картинка к посту: словарь макетов, спецификация карточки и вердикт «рисовать / не
 * рисовать». docs/phase-2/08-visuals.md §2, §3, §5.
 *
 * ПОЧЕМУ ЭТО КОНТРАКТ, А НЕ ТИП СЕРВИСА. Интерфейс обязан подписать полосу визуала именем
 * макета («макет сравнение») и объяснить, почему картинки нет; из наших пакетов ему доступен
 * только `@smm/contracts` (правило R4). Зависимости пакета при этом не растут: здесь один
 * zod, как и было (правило R1).
 *
 * ПОЧЕМУ СХЕМА, А НЕ ПРАВИЛА В ПРОМПТЕ (М1, §2.2). Картинка — это ЗАПОЛНЕННАЯ СТРУКТУРА, а
 * не намерение. Каждое «когда картинка не берётся» из §2.2 записано здесь отказом разбора:
 * сравнение с одним субъектом, сравнение, в котором субъекты различаются одним признаком,
 * порядок из двух шагов, цифра без базы — всё это не проходит `safeParse` ДО того, как
 * что-либо решит код. Модель может написать убедительное обоснование; выдумать второй
 * различающий признак, которого нет в посте, она не может.
 *
 * ЧЕГО ЗДЕСЬ НАМЕРЕННО НЕТ. Проверки М2 («на карточке нет ничего, чего нет в посте»): ей
 * нужен текст поста и материалы темы, то есть она живёт рядом со своим единственным
 * вызывающим — в сервисе генерации. Схема отвечает за форму, М2 — за правдивость.
 */

import * as z from 'zod';
import { codePointLen } from '../units.ts';

/* ─────────────────────────── словари ─────────────────────────── */

/**
 * Список ЗАКРЫТ, но не пуст на три (решение Ильи 06.09.2026 §13 п.2, расширено 08.09.2026).
 * «Почти сравнение» по-прежнему отказ: закрытость означает, что макет не заводится по ходу
 * заполнения, а не что их навсегда три.
 *
 * ЧЕТВЁРТЫМ СТАЛ `list`, и вот почему именно он. Три первых макета требуют форм данных,
 * которых в потоке канала почти не бывает, и за две недели в канал не вышло НИ ОДНОЙ
 * карточки. При этом самый частый отказ — «набор, выданный за порядок»: сводка недели, три
 * совета, список возможностей. Отказ верный, пока единственная форма для перечисления —
 * `sequence`: нумеровать непорядок нельзя, номера соврали бы про порядок, которого нет.
 * Но карточка, которая порядка НЕ ЗАЯВЛЯЕТ, не врёт ничем, и запрещать её было нечем, кроме
 * отсутствия макета.
 *
 * ЗАМКИ ПРИ ЭТОМ НЕ ТРОНУТЫ: на карточку по-прежнему не попадает ничего, чего нет в посте
 * (ВИЗ-6), опровергающий проход по-прежнему снимает карточку, которая пересказывает строку
 * поста, и именно он не даёт `list` стать украшением. Добавлена ФОРМА, а не послабление.
 *
 * ПЯТЫЙ — `cover`, и он ТОЛЬКО ПО ПРОСЬБЕ ЧЕЛОВЕКА (08.09.2026, просьба Ильи). Обложка —
 * надзаголовок и одна строка лида — это ровно «пересказ лида другим шрифтом», первый пример
 * отказа §2, и автономный прогон её не выносит никогда: опровергающий проход снял бы её по
 * построению, а `decide.ts` отбивает её до него. Но когда человек нажал «Нарисовать
 * картинку», он хочет картинку к посту, а не спор о том, есть ли в посте таблица, — и
 * обложка становится тем, что рисуется, когда данных под макет нет. Лид на ней — ДОСЛОВНЫЙ
 * фрагмент поста, сверяемый подстрокой: второго автора на картинке по-прежнему нет.
 */
export const VISUAL_LAYOUTS = ['contrast', 'sequence', 'stat', 'list', 'cover'] as const;
/** Макеты, которые прогон выносит САМ. `cover` сюда не входит — см. выше. */
export const VISUAL_AUTONOMOUS_LAYOUTS = ['contrast', 'sequence', 'stat', 'list'] as const;
export const VisualLayoutSchema = z.enum(VISUAL_LAYOUTS);
export type VisualLayout = z.output<typeof VisualLayoutSchema>;

/**
 * Вердикт в том виде, в каком он ложится в `draft_version.visual_verdict` и в каком его
 * произносит модель. Слово ОДНО на оба конца намеренно: пара «модель говорит draw, база
 * хранит drawn» — это словарь-переводчик в трёх местах и рассинхрон в четвёртом.
 */
export const VISUAL_VERDICTS = ['drawn', 'none'] as const;
export const VisualVerdictSchema = z.enum(VISUAL_VERDICTS);
export type VisualVerdict = z.output<typeof VisualVerdictSchema>;

/** Состояние предложения картинки (§6.4, вариант «в»). */
export const VISUAL_PROPOSAL_STATES = ['pending', 'accepted', 'rejected', 'superseded'] as const;
export const VisualProposalStateSchema = z.enum(VISUAL_PROPOSAL_STATES);
export type VisualProposalState = z.output<typeof VisualProposalStateSchema>;

/** Кто положил байты: сервис генерации или человек из браузера. */
export const MEDIA_ORIGINS = ['writer', 'human'] as const;
export const MediaOriginSchema = z.enum(MEDIA_ORIGINS);
export type MediaOrigin = z.output<typeof MediaOriginSchema>;

/* ─────────────────────────── размеры ─────────────────────────── */

/**
 * Геометрия карточки — КОНСТАНТА, а не поле формата (§3). Горизонтальный полноэкранный
 * кадр: 1920+1080 = 3000 при телеграмном потолке суммы 10 000 и соотношение 1.78 при
 * потолке 20 (`../media.ts`), то есть два порядка запаса на той стороне, которая связывает.
 */
export const VISUAL_WIDTH = 1920;
export const VISUAL_HEIGHT = 1080;

/**
 * Потолок ЯЧЕЙКИ, кодпоинты. Он не стилистический, а структурный: у SVG нет flexbox, и
 * длинная строка не переносится и не сжимается — она молча уезжает за край, а растеризатор
 * об этом не сообщает (§4.2б). Слово «максимально минимально» §3 здесь становится числом.
 */
export const VISUAL_CELL_MAX = 28;
/** Потолок СТРОКИ-фразы (что меряем, база сравнения). Длиннее ячейки, короче предложения. */
export const VISUAL_LINE_MAX = 64;
/** Потолок лида на обложке: три строки крупным кеглем, и это фрагмент поста, а не абзац. */
export const VISUAL_HEADLINE_MAX = 96;
/** Потолок подписи источника — брендбук требует его видимым, а не мелким. */
export const VISUAL_SOURCE_MAX = 40;
/** Сумма ВСЕХ строк, попадающих на карточку. §2.3: карточка — не пересказ поста. */
export const VISUAL_TEXT_TOTAL_MAX = 180;
/**
 * Потолок ШИРОКИХ форм — списка и сравнения из четырёх предметов. Почему их два и когда
 * берётся который — в `visualTextTotalMaxOf` ниже, там же и причина.
 */
export const VISUAL_TEXT_TOTAL_MAX_WIDE = 260;

/**
 * Потолок суммы строк — ПО ФОРМЕ КАРТОЧКИ, а не один на всех. Одно число для всех макетов
 * запрещало арифметикой то, что разрешала вёрстка, и оба случая уже встретились:
 *
 *   · СПИСОК стоит во всю ширину, его строка вмещает ~60 знаков против 28 в колонке;
 *   · СРАВНЕНИЕ ИЗ ЧЕТЫРЁХ предметов — это 12 ячеек вместо 9, то есть на треть больше текста
 *     при той же плотности. Живое сравнение четырёх моделей по трём признакам набирает ~200
 *     знаков и отбивалось бы бюджетом ВСЕГДА, каким бы коротким ни было. Поднятый потолок
 *     предметов без поднятого потолка текста — правка, которая ничего не меняет.
 *
 * Смысл §2.3 при этом цел: 260 знаков против поста в полторы-две тысячи — по-прежнему
 * карточка, а не пересказ. И настоящий сторож не здесь, а в вёрстке: строка, не влезшая в
 * свою коробку, отбивается `does_not_fit` с указанием, какая именно.
 */
export function visualTextTotalMaxOf(spec: {
  readonly kind: string;
  readonly subjects?: readonly unknown[] | undefined;
}): number {
  if (spec.kind === 'list') return VISUAL_TEXT_TOTAL_MAX_WIDE;
  if (spec.kind === 'contrast' && (spec.subjects?.length ?? 0) >= 4) {
    return VISUAL_TEXT_TOTAL_MAX_WIDE;
  }
  return VISUAL_TEXT_TOTAL_MAX;
}
/**
 * Потолок причины вердикта и строки «картинка заменяет…». 500 — потолок хранения
 * (`draft_version.visual_reason`), одно число на схему, тело intake и CHECK в базе.
 */
export const VISUAL_REASON_MAX = 500;

/**
 * Что подставляется вместо ПУСТОГО поля описания, когда человек нажал «Нарисовать
 * картинку» и ничего не написал (правка 10 от 2026-09-08, §8.1). Просьба «нарисуй, я не
 * знаю что» — законная: человек прочитал пост и видит, что картинка ему пойдёт, а
 * раскладывать факты по макету — работа решателя, а не его.
 *
 * ЛИТЕРАЛ, А НЕ ПРАВКА СХЕМЫ И НЕ 400. Описание обязательно в трёх местах сразу: кнопка
 * заблокирована на пустом поле, сервер отбивает пустой `prompt` четырёхсотым, и в базе
 * стоит `CHECK (length(prompt) BETWEEN 1 AND 2000)` (`rework_request`, 0001). Трогать эту
 * тройку ради «поле можно не заполнять» значит ослаблять её для ВСЕХ переделок, включая
 * текстовые, где пустой комментарий — это баг интерфейса. Подстановка одной известной
 * строки решает задачу, ничего не ослабляя.
 *
 * ПРЕФИКС `auto:` НЕСУЩИЙ. По нему сервис генерации узнаёт третье состояние входа решателя
 * — `humanAsked`: обоснование фактов остаётся `required` (человек фактов не приносил, и
 * ослаблять проверку происхождения нечем — ВИЗ-6), а опровергающий проход пропускается
 * (человек попросил явно). Просто «пустая строка» этого состояния не выражает: она
 * неотличима от человеческого описания, которое потерялось по дороге.
 *
 * ПЕРЕРИСОВКА ЭТОГО НЕ ПОЛУЧАЕТ: там поле остаётся обязательным. Пустая перерисовка
 * означает «попробуй ещё раз без новой информации» — модель почти наверняка вернёт тот же
 * вердикт, версия не создастся, и черновик застрянет «на переделке».
 */
export const VISUAL_AUTO_PROMPT = 'auto: нарисуй по фактам поста';

/* ─────────────────────────── алфавит ─────────────────────────── */

/**
 * ЗАКРЫТЫЙ алфавит карточки. Отказ по символу — это отказ от карточки, а не замена символа:
 * кодпоинт, которого нет в гарнитуре, рисуется прямоугольником .notdef, и ни растеризатор,
 * ни тест байтов об этом не скажут. Что исключено по построению: эмодзи и всё вне BMP,
 * управляющие символы, стрелки (их нет в части гарнитур), `<` и `>` — второй замок к
 * экранированию шаблона.
 */
export const VISUAL_CHARSET_RE = /^[A-Za-zА-Яа-яЁё0-9 .,:;!?()«»„“”"'’—–\-+×÷=%№…·°/@#&*]+$/u;

export function isVisualText(s: string): boolean {
  return VISUAL_CHARSET_RE.test(s);
}

/**
 * Длина в КОДПОИНТАХ, а не в UTF-16 code units, которыми меряет `.max()` у zod. Сегодня
 * алфавит выше не пускает ни одного суррогатного символа, и две единицы совпадают — но
 * совпадение это следствие алфавита, а не свойство длины, и алфавит однажды расширят.
 */
function cpLen(s: string): number {
  return codePointLen(s) as unknown as number;
}

function boundedText(min: number, max: number) {
  // `.min`/`.max` стоят рядом с проверкой кодпоинтов не ради дублирования: только они
  // попадают в JSON Schema строгого вывода, то есть только их видит модель. Для алфавита
  // выше обе единицы совпадают, а на суррогатах UTF-16 строже — то есть подсказка модели
  // никогда не разрешает больше, чем пропускает разбор.
  return z
    .string()
    .min(min)
    .max(max)
    .refine((s) => cpLen(s) >= min && cpLen(s) <= max, {
      error: `ожидалось ${String(min)}..${String(max)} кодпоинтов`,
    })
    .refine(isVisualText, { error: 'символ вне алфавита карточки' });
}

/** Ячейка макета: имя субъекта, признак, значение, шаг. */
const cell = boundedText(2, VISUAL_CELL_MAX);
/** Фраза: то, что меряет цифра, и база сравнения. */
const phrase = boundedText(2, VISUAL_LINE_MAX);
/** Подпись источника. Брендбук требует её видимой у каждой цифры. */
const source = boundedText(2, VISUAL_SOURCE_MAX);

const reasonText = z
  .string()
  .min(10)
  .max(VISUAL_REASON_MAX)
  .refine((s) => cpLen(s) >= 10 && cpLen(s) <= VISUAL_REASON_MAX, {
    error: `ожидалось 10..${String(VISUAL_REASON_MAX)} кодпоинтов`,
  });

/* ─────────────────────────── макеты ─────────────────────────── */

/**
 * СРАВНЕНИЕ. Матрица `cells` — это строки-субъекты × столбцы-признаки, а не список у
 * каждого субъекта: у сравнения признаки ОБЩИЕ, и структура обязана это утверждать, а не
 * надеяться на дисциплину заполняющего.
 *
 * Два условия, и второе — то самое место, где умирает карточка «было / стало» из §2.8:
 * различающих признаков должно быть не меньше ДВУХ. Один общий признак — это список, а не
 * сравнение, и никакое обоснование этого не меняет.
 */
export const ContrastSpecSchema = z
  .strictObject({
    kind: z.literal('contrast'),
    /** Надзаголовок-метка. Регистр поднимает ШАБЛОН, а не заполняющий. */
    eyebrow: cell,
    // ЧЕТЫРЁХ ДОПУСКАЕТ СХЕМА, А ВЛЕЗАЕТ ЛИ — РЕШАЕТ ВЁРСТКА. Потолок подняли с трёх
    // 08.09.2026: пост, называющий четыре модели, не мог быть нарисован ВООБЩЕ, каким бы
    // хорошим ни было сравнение, и это был запрет схемы, а не решение о карточке. Колонка
    // при четырёх предметах — 434 px против 578, поэтому длинные имена теперь отбивает
    // `does_not_fit` с понятной причиной. Это правильное место для такого отказа: он про
    // конкретные строки, а не про число предметов.
    subjects: z.array(cell).min(2).max(4),
    facets: z.array(cell).min(2).max(4),
    /** subjects.length строк × facets.length столбцов. */
    cells: z.array(z.array(cell)),
    source,
  })
  .check((ctx) => {
    const v = ctx.value;
    const push = (path: (string | number)[], message: string): void => {
      ctx.issues.push({ code: 'custom', input: v, path, message });
    };
    if (v.cells.length !== v.subjects.length) {
      push(['cells'], 'contrast_grid_shape_mismatch');
      return;
    }
    for (const [i, row] of v.cells.entries()) {
      if (row.length !== v.facets.length) {
        push(['cells', i], 'contrast_grid_shape_mismatch');
        return;
      }
    }
    let distinguishing = 0;
    for (let j = 0; j < v.facets.length; j++) {
      const column = v.cells.map((row) => row[j] as string);
      if (new Set(column).size === column.length) distinguishing += 1;
    }
    if (distinguishing < 2) push(['cells'], 'contrast_not_a_comparison');
  });
export type ContrastSpec = z.output<typeof ContrastSpecSchema>;

/**
 * ПОРЯДОК. Шаги переставляются без потери смысла — это набор, а не порядок, и картинки нет.
 * `order_evidence` — дословный фрагмент поста, НАЗЫВАЮЩИЙ порядок; на карточку он не
 * попадает и в бюджет текста не входит: это доказательство для проверки, а не строка макета.
 * Поэтому у него нет ни алфавита карточки, ни её потолка длины — он цитата поста.
 */
export const SequenceSpecSchema = z
  .strictObject({
    kind: z.literal('sequence'),
    eyebrow: cell,
    steps: z.array(cell).min(3).max(5),
    order_evidence: z
      .string()
      .min(10)
      .max(200)
      .refine((s) => cpLen(s) >= 10 && cpLen(s) <= 200, {
        error: 'ожидалось 10..200 кодпоинтов',
      }),
    source,
  })
  .check((ctx) => {
    const v = ctx.value;
    if (new Set(v.steps).size !== v.steps.length) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['steps'],
        message: 'sequence_steps_not_distinct',
      });
    }
  });
export type SequenceSpec = z.output<typeof SequenceSpecSchema>;

/**
 * ЦИФРА. Все пять полей обязательны и ни одно не nullable — §2.2: цифра без базы сравнения
 * не берётся («выросло на 30%» — с чего и за какой срок), а брендбук запрещает цифру без
 * видимого источника. База обязана СОДЕРЖАТЬ ЧИСЛО: «по сравнению с прошлым годом» — это
 * не база, это оборот речи.
 */
export const StatSpecSchema = z.strictObject({
  kind: z.literal('stat'),
  eyebrow: cell,
  /** Само число: цифры, пробелы (в том числе неразрывные), точка и запятая. */
  value: z.string().regex(/^[0-9][0-9   .,]{0,6}$/, 'значение — только число'),
  unit: boundedText(1, 8),
  measures: phrase,
  baseline: phrase.refine((s) => /\d/.test(s), { error: 'база сравнения без числа — не база' }),
  source,
});
export type StatSpec = z.output<typeof StatSpecSchema>;

/**
 * СПИСОК. Перечисление 3–5 пунктов, у которого порядок НЕ ЗАЯВЛЕН ничем — ни номерами, ни
 * линией, ни словом «сначала». В этом вся разница с `sequence`, и она не косметическая:
 * `sequence` УТВЕРЖДАЕТ порядок и потому требует доказательства (`order_evidence`), а список
 * не утверждает ничего, кроме того, что эти пункты в посте названы. Доказывать тут нечего,
 * поэтому поля-улики у него нет и быть не должно.
 *
 * Пункты РАЗНЫЕ — иначе это не перечисление, а повтор с опечаткой. Проверка дешёвая и ловит
 * ровно тот случай, когда модель добивает список до трёх пунктов, растиражировав один.
 */
export const ListSpecSchema = z
  .strictObject({
    kind: z.literal('list'),
    eyebrow: cell,
    items: z.array(phrase).min(3).max(5),
    source,
  })
  .check((ctx) => {
    const v = ctx.value;
    const norm = v.items.map((s) => s.trim().toLowerCase());
    if (new Set(norm).size !== norm.length) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['items'],
        message: 'list_items_not_distinct',
      });
    }
  });
export type ListSpec = z.output<typeof ListSpecSchema>;

/**
 * ОБЛОЖКА. Надзаголовок, лид, источник — и ничего, что нужно доказывать: лид обязан быть
 * фрагментом поста, и это проверяет гейт подстрокой, как `order_evidence`. Сама схема
 * держит только форму.
 */
export const CoverSpecSchema = z.strictObject({
  kind: z.literal('cover'),
  eyebrow: cell,
  headline: boundedText(8, VISUAL_HEADLINE_MAX),
  source,
});
export type CoverSpec = z.output<typeof CoverSpecSchema>;

/** Строки, которые ДЕЙСТВИТЕЛЬНО попадают на карточку, — для бюджета текста. */
function drawnStrings(spec: { kind: string; [k: string]: unknown }): readonly string[] {
  const out: string[] = [];
  const add = (v: unknown): void => {
    if (typeof v === 'string') out.push(v);
    else if (Array.isArray(v)) for (const x of v) add(x);
  };
  for (const [key, value] of Object.entries(spec)) {
    // `kind` — дискриминант, `order_evidence` — цитата поста; на карточке нет ни того,
    // ни другого.
    if (key === 'kind' || key === 'order_evidence') continue;
    add(value);
  }
  return out;
}

/** Сумма строк карточки в кодпоинтах — то, что ограничивает `VISUAL_TEXT_TOTAL_MAX`. */
export function visualTextTotal(spec: VisualSpec): number {
  return drawnStrings(spec).reduce((n, s) => n + cpLen(s), 0);
}

/**
 * Спецификация карточки. Дискриминант — `kind`, и ПЯТОЙ формы нет: «почти contrast» —
 * это отсутствие картинки, а не новый макет.
 */
export const VisualSpecSchema = z
  .discriminatedUnion('kind', [
    ContrastSpecSchema,
    SequenceSpecSchema,
    StatSpecSchema,
    ListSpecSchema,
    CoverSpecSchema,
  ])
  .check((ctx) => {
    const total = drawnStrings(ctx.value).reduce((n, s) => n + cpLen(s), 0);
    const max = visualTextTotalMaxOf(ctx.value);
    if (total > max) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: [],
        message: `visual_text_budget: ${String(total)} > ${String(max)}`,
      });
    }
  });
export type VisualSpec = z.output<typeof VisualSpecSchema>;

/* ─────────────────────── вердикт как объект ─────────────────────── */

/**
 * ОТВЕТ ОТДЕЛЬНОГО ВЫЗОВА «рисовать или нет» (М3, §2.4). Умолчание этого вызова — отказ,
 * и форма ответа это утверждает: `none` не требует ничего, кроме причины, а `drawn` требует
 * заполненного макета целиком. Сказать «да» из вежливости дискриминированному объединению
 * нельзя — под «да» надо предъявить структуру.
 *
 * `reason` есть у ОБЕИХ ветвей. У `none` его показывает полоса визуала («сервис решил не
 * рисовать, вот почему» — §6.2), у `drawn` он остаётся следом решения в версии: доля §2.6
 * считается по вердикту, а разбирать спорную карточку приходится по причине.
 */
export const VisualDecisionSchema = z.discriminatedUnion('verdict', [
  z.strictObject({
    verdict: z.literal('none'),
    reason: reasonText,
  }),
  z.strictObject({
    verdict: z.literal('drawn'),
    reason: reasonText,
    /** Строка поста, которую карточка заменяет собой. Её показывает полоса визуала. */
    instead_of_text: reasonText,
    spec: VisualSpecSchema,
  }),
]);
export type VisualDecision = z.output<typeof VisualDecisionSchema>;

/**
 * ОТВЕТ ОПРОВЕРГАЮЩЕГО ПРОХОДА (М4, §2.5). Ответ — ЦИТАТА, а не суждение, и именно поэтому
 * механизм работает: цитату можно проверить подстрокой по тексту поста, а «нет, картинка
 * полезна» — нельзя. Пустая строка означает «такой строки в посте нет».
 */
export const VisualRefutationSchema = z.strictObject({
  same_line: z.string().max(300),
});
export type VisualRefutation = z.output<typeof VisualRefutationSchema>;

/**
 * Те же схемы как JSON Schema для строгого структурированного вывода — по той же причине,
 * что и `WRITER_OUTPUT_JSON_SCHEMA`: схема ответа и схема разбора обязаны быть одним
 * объектом. Пользовательские проверки (длина в кодпоинтах, алфавит, форма матрицы) в JSON
 * Schema не выражаются и сюда не попадают — их держит разбор, и это правильное разделение:
 * подсказка модели не обязана быть замком, а замок обязан быть.
 */
/**
 * Приставка причины, которой вердикт помечает СВОЮ ПОЛОМКУ, а не решение не рисовать.
 *
 * Живёт в контрактах, потому что у неё два конца: пишет её сервис генерации, читает экран
 * одобрения, и договориться они обязаны об одной строке. Пока строка была только у писателя,
 * интерфейс подписывал «это штатный исход, а не сбой» под текстом четырёхсотки от API —
 * то есть уверял человека, что сломанное работает.
 *
 * Разница не косметическая. Здоровый отказ — редакционное решение: рисовать нечего. Сломанный
 * механизм означает, что картинки не будет НИ У ОДНОГО поста, и доля §2.6 при этом выглядит
 * идеально здоровой. Вырождение прячется именно так.
 */
export const MECHANISM_REASON_PREFIX = 'механизм вердикта:';

/**
 * СХЕМА ДЛЯ МОДЕЛИ — ПЛОСКАЯ, И ЭТО ВЫНУЖДЕННО. `z.toJSONSchema` от дискриминированного
 * объединения даёт `oneOf`, а структурированный вывод Anthropic его не принимает:
 *   400 invalid_request_error — «output_config.format.schema: Schema type 'oneOf' is not
 *   supported» (получено на живом прогоне 07.09.2026, req_011CepDmQ71TS6RxhcbYCK9d).
 * Объединений в ответе два — вердикт и макет, — и оба схлопнуты здесь в один объект с
 * необязательными полями.
 *
 * НА ЗАМОК ЭТО НЕ ВЛИЯЕТ, и в этом весь смысл разделения. Модели уходит ПОДСКАЗКА, а
 * разбирает ответ строгий `VisualDecisionSchema` (см. `decide.ts`), для которого «drawn»
 * без заполненного макета — не вердикт, а мусор, то есть отказ. Ослабление подсказки не
 * ослабляет проверку: сказать «да» из вежливости по-прежнему нельзя, под «да» надо
 * предъявить структуру — просто отказ теперь наступает на разборе, а не на границе API.
 *
 * Пишется РУКАМИ, а не выводится: генератор снова вернул бы `oneOf`. Тест сверяет набор
 * полей с объединением, поэтому разъехаться молча они не могут.
 */
export const VISUAL_DECIDE_JSON_SCHEMA: Record<string, unknown> = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  additionalProperties: false,
  required: ['verdict', 'reason'],
  properties: {
    verdict: {
      type: 'string',
      enum: ['none', 'drawn'],
      description: 'none — рисовать нечего. drawn — только если ниже заполнен spec целиком.',
    },
    reason: { type: 'string', description: `Почему. 10–${String(VISUAL_REASON_MAX)} знаков.` },
    instead_of_text: {
      type: 'string',
      description:
        'Только при drawn: строка поста, которую карточка заменяет собой. ' +
        `10–${String(VISUAL_REASON_MAX)} знаков.`,
    },
    spec: {
      type: 'object',
      additionalProperties: false,
      required: ['kind', 'eyebrow', 'source'],
      description:
        'Только при drawn. Заполняются поля ТОЛЬКО своего kind; чужие не присылать. ' +
        `Каждая строка карточки — 2–${String(VISUAL_CELL_MAX)} знаков, сумма всех строк — ` +
        `не больше ${String(VISUAL_TEXT_TOTAL_MAX)}. У list и у сравнения из четырёх ` +
        `предметов строка до ${String(VISUAL_LINE_MAX)}, а сумма — до ` +
        `${String(VISUAL_TEXT_TOTAL_MAX_WIDE)}.`,
      properties: {
        kind: {
          type: 'string',
          enum: [...VISUAL_LAYOUTS],
          description:
            'contrast — сравнение 2–4 предметов по 2–4 ОБЩИМ признакам; ' +
            'sequence — 3–5 шагов, порядок которых назван в посте; ' +
            'stat — одна цифра с базой сравнения; ' +
            'list — 3–5 названных в посте пунктов, порядок которых НЕ заявляется; ' +
            'cover — ТОЛЬКО по просьбе человека: надзаголовок и дословный фрагмент лида.',
        },
        eyebrow: { type: 'string', description: `Надзаголовок, 2–${String(VISUAL_CELL_MAX)}.` },
        source: {
          type: 'string',
          description: `Подпись источника, 2–${String(VISUAL_SOURCE_MAX)}.`,
        },
        subjects: {
          type: 'array',
          items: { type: 'string' },
          description: 'contrast: 2–4 предмета сравнения.',
        },
        headline: {
          type: 'string',
          description:
            `cover: лид карточки, 8–${String(VISUAL_HEADLINE_MAX)} знаков — ДОСЛОВНЫЙ фрагмент ` +
            'поста (его лид или заголовок). Сверяется с текстом подстрокой.',
        },
        items: {
          type: 'array',
          items: { type: 'string' },
          description:
            'list: 3–5 пунктов, названных в посте. Порядок не заявляется и не важен — ' +
            'порядок называет sequence. Пункты обязаны быть РАЗНЫМИ.',
        },
        facets: {
          type: 'array',
          items: { type: 'string' },
          description:
            'contrast: 2–4 признака, ОБЩИЕ для всех предметов. Если у предметов разные ' +
            'признаки — это не сравнение, и вердикт обязан быть none.',
        },
        cells: {
          type: 'array',
          items: { type: 'array', items: { type: 'string' } },
          description: 'contrast: матрица subjects × facets, строка на предмет.',
        },
        steps: {
          type: 'array',
          items: { type: 'string' },
          description: 'sequence: 3–5 шагов в порядке, названном постом.',
        },
        order_evidence: {
          type: 'string',
          description:
            'sequence: ДОСЛОВНЫЙ фрагмент поста, называющий порядок. На карточку не попадает.',
        },
        value: { type: 'string', description: 'stat: само число, только цифры и разделители.' },
        unit: { type: 'string', description: 'stat: единица, 1–8 знаков.' },
        measures: { type: 'string', description: 'stat: что именно меряет цифра.' },
        baseline: {
          type: 'string',
          description: 'stat: база сравнения, и в ней ОБЯЗАНА быть цифра. Без базы — none.',
        },
      },
    },
  },
};

export const VISUAL_REFUTE_JSON_SCHEMA: Record<string, unknown> = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  additionalProperties: false,
  required: ['same_line'],
  properties: {
    same_line: {
      type: 'string',
      description:
        'Дословная строка поста, говорящая то же, что карточка. Пустая строка — такой ' +
        'строки в посте нет. Не больше 300 знаков.',
    },
  },
};
